import i18next from "i18next";
import {getWidth, PSL_COL_RESIZE} from "../KumaView/utils";
import {EOBColumnKey} from "../BaseOptionsView/BaseOptionsTableUtils";
import {ITextExportStatus} from "src/components/Table/VirtualizedTableInterfaces";
import React from "react";
import {buildTextExportStatusComponent} from "src/components/Table/VirtualizedTableUtils";
import {TwoLanguageCompareInput} from "src/components/Table/components/twoLanguage/TwoLanguageCompareInput";
import {TwoLanguageCompareTextArea} from "src/components/Table/components/twoLanguage/TwoLanguageCompareTextArea";
import TwoLanguageTextArea from "src/components/Table/components/twoLanguage/TwoLanguageTextArea";
import {Checkbox} from "semantic-ui-react";
import Derivate from "src/components/Table/components/derivate/Derivate";
import {MissingPDiPAMTBsKey, PropertiesKey} from "./OptionsViewUtils";
import DerivateHeader from "src/components/Table/components/headerComponents/DerivateHeader";
import TextArea from "src/components/Table/components/basic/TextArea";
import CommentSuggestionsTextArea from "src/components/Table/components/basic/CommentSuggestionsTextArea";
import DateInput from "src/components/Table/components/basic/DateInput";
import {OnClickInput} from "src/components/Table/components/modalDropdown/OnClickInput";
import PriceInput from "src/components/Table/components/basic/PriceInput";
import TextInput from "src/components/Table/components/basic/TextInput";
import TextFlagCell from "src/components/Table/components/basic/TextFlagCell";
import NewWorkFlowState from "src/components/Table/components/workflow/WorkFlowStateNewGrid";
import TextFieldFilter from "src/components/AgGridFilter/TextFieldFilter";
import AgDateFilter from "src/components/AgGridFilter/DateFilter";
import DropDownFilter from "src/components/AgGridFilter/DropdownFilter";
import {IPSLApproval} from "src/services/WorkflowUtils";
import OptionIndicatorCell from "src/components/Table/components/basic/optionIndicator/OptionIndicatorCell";
import PCCDTextComparePopup from "./PCCDTextComparePopup";
import {getDeAiFlags} from "src/components/Table/components/twoLanguage/AWSTranslatePreviewHandler";
import {HeaderSelectionState} from "./newGridSelection";

const createTextFieldFilter = (field: string, t: any, onNewFilterChange: (field: string, value: string) => void) => ({
    filter: true,
    floatingFilter: true,
    floatingFilterComponent: TextFieldFilter,
    floatingFilterComponentParams: {
        suppressFilterButton: true,
        onNewFilterChange: onNewFilterChange,
        field: field,
        t
    },
});
const createDropdownFilter = (
    field: string,
    t: any,
    onNewFilterChange: (field: string, value: string) => void,
    dropdownFilters: any,
    mapFunction: (item: any) => {key: string; value: string; text: string},
    isCustomerFilter: boolean = false
) => {
    return {
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: DropDownFilter,
        floatingFilterComponentParams: {
            suppressFilterButton: true,
            onNewFilterChange: onNewFilterChange,
            field: field,
            dataSource: !isCustomerFilter && dropdownFilters && dropdownFilters[field]
                ? dropdownFilters[field].map(mapFunction) // Use the provided map function
                : isCustomerFilter && dropdownFilters
                    ? dropdownFilters.map(mapFunction)
                    : [], // Fallback to an empty array if the key is missing
            width: '200px',
            t
        },
    };
};
const createDateFieldFilter = (
    field: string,
    t: any,
    onNewFilterChange: (field: string, value: string, operator: string) => void
) => ({
    filter: true,
    floatingFilter: true,
    floatingFilterComponent: AgDateFilter,
    floatingFilterComponentParams: {
        suppressFilterButton: true,
        onNewFilterChange: onNewFilterChange,
        field: field,
        t
    },
});

export const PSL_SELECTION_COL_ID = 'selection';

export interface IPSLSelectionColumnCallbacks {
    isRowSelected: (objectId: number) => boolean;
    onToggleRow: (data: any, checked: boolean) => void;
    disabled?: boolean;
    headerState?: () => HeaderSelectionState;
    onToggleAll?: (checked: boolean) => void;
    headerDisabled?: boolean;
}

const PSLSelectionHeader = (selection?: IPSLSelectionColumnCallbacks) => function PSLSelectionHeaderComponent() {
    if (!selection || !selection.headerState || !selection.onToggleAll) {
        return <span/>;
    }
    const state = selection.headerState();
    const onToggleAll = selection.onToggleAll;
    const title = state === 'all'
        ? i18next.t("PSL_MULTI_DLT:UNSELECT_ALL_TOOLTIP", {defaultValue: "Unselect all"})
        : i18next.t("PSL_MULTI_DLT:SELECT_ALL_TOOLTIP", {defaultValue: "Select all options matching the current filter"});
    return (
        <div
            className="psl-selection-header"
            style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%'}}
            title={title}
        >
            <Checkbox
                checked={state === 'all'}
                indeterminate={state === 'some'}
                disabled={selection.disabled === true || selection.headerDisabled === true}
                onChange={() => onToggleAll(state !== 'all')}
            />
        </div>
    );
};

export const getPSLColumnDefs = (
    props: any,
    visibleDerivates: any,
    isSpecificPSL: boolean,
    onNewFilterChange: (field: string, value: string) => void,
    dropdownFilters: any,
    flagsOptions: any,
    visibleColumns: any,
    globalSearch: string,
    onDataChanged: (key: string, rowNumber: number, newValue: any) => void,
    handleOnCellClick: (key: string, index: number, value: any, visibleDerivates?: string[]) => void,
    isCellReadOnly?: (key: string, data: any, readOnly: boolean) => boolean,
    workFlowResetStateDisabled?: (key: string, data: any, readOnly: boolean) => boolean,
    onApprove?: (newValue: IPSLApproval, id: number) => void,
    onChangeWorkflow?: (key: string, objectId: any, index: number) => void,
    onOptionWFReset?: (index: number) => void,
    selection?: IPSLSelectionColumnCallbacks,
) => {
    if (!visibleColumns || visibleColumns.length === 0) {
        // If no visible columns are provided, return an empty array
        visibleColumns = [];
    }
    // Extract keys from visibleColumns for easier lookup
    const visibleColumnKeys = visibleColumns.map((column: any) => column.key);

    // F4: the edit-dialog labels depend only on the current language, so build them ONCE here
    // instead of rebuilding the whole object (11-13 i18n lookups) inside every cell renderer on
    // every cell render. The per-row fields (optionCode / editingColumn / optionType / pdipText)
    // are still spread in per cell, so behaviour is identical.
    const editDialogLabels = {
        dialogTitle: props.t('OPTIONS_VIEW:EDIT_OPTION'),
        previewAiHeader: props.t('GLOBAL:GLOBAL_AI_TRANSLATE_HEADER'),
        previewAiSubHeader: props.t('GLOBAL:GLOBAL_AI_TRANSLATE_SUB_HEADER'),
        previewAiOriginal: props.t('GLOBAL:GLOBAL_AI_TRANSLATE_ORIGINAL'),
        previewAiPreview: props.t('GLOBAL:GLOBAL_AI_TRANSLATE_PREVIEW'),
        previewAiConfirm: props.t('GLOBAL:GLOBAL_AI_TRANSLATE_CONFIRM'),
        previewAiCancel: props.t('GLOBAL:CANCEL'),
        previewAiConfimChange: props.t('GLOBAL:GLOBAL_AI_TRANSLATE_CONFIRM_CHANGES'),
        previewAiGrammarSubHeader: props.t('GLOBAL:GLOBAL_AI_TRANSLATE_GRAMMAR_SUB_HEADER'),
        tooltipAiTranslate: props.t('GLOBAL:GLOBAL_AI_TRANSLATE_TOOLTIP'),
        tooltipAiGrammar: props.t('GLOBAL:GLOBAL_AI_TRANSLATE_TOOLTIP_GRAMMAR'),
    };
    // Extra labels used only by the longtext column (generate feature).
    const editDialogGenerateLabels = {
        previewAiGenerateSubHeader: props.t('GLOBAL:GLOBAL_AI_GENERATE_SUBHEADER'),
        tooltipAiGenerate: props.t('GLOBAL:GLOBAL_AI_TOOLTIP_GENERATE'),
    };

    return [
        {
            field: '',
            key: '',
            colId: PSL_SELECTION_COL_ID,
            headerName: '',
            width: getWidth('objectId', PSL_COL_RESIZE),
            sortable: false,
            editable: false,
            suppressMenu: true,
            cellClass: 'ag-grid-row-style',
            hide: false, // Set hide based on visibleColumns
            headerComponent: PSLSelectionHeader(selection),
            cellRenderer: function (params: any) {
                if (!params.data || !selection || params.data.objectId === undefined) {
                    return '';
                }
                const objectId: number = params.data.objectId;
                return (
                    <div className="psl-selection-cell" style={{display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%'}}>
                        <Checkbox
                            checked={selection.isRowSelected(objectId)}
                            disabled={selection.disabled === true}
                            onChange={(e: any, v: any) => selection.onToggleRow(params.data, v.checked === true)}
                        />
                    </div>
                );
            },
        },
        {
            field: 'categoryFilters',
            key: 'categoryText',
            headerName: props.t("COLUMN_FACTORY:CATEGORY"),
            width: getWidth('categoryFilters', PSL_COL_RESIZE),
            sortable: false,
            editable: false,
            cellClass: 'ag-grid-row-style',
            hide: !visibleColumnKeys.includes('categoryText'), // Set hide based on visibleColumns
            cellRenderer: function (params: any) {
                if (params.data) {
                    return (
                        <div style={{width: '100%'}}>
                            <TextInput
                                value={params.data.categoryText}
                                onChange={() => {
                                    // No inline editing
                                }}
                                readOnly={true}
                                uglyMode={false}
                                rowIndex={0}
                                t={props.t}
                                language={props.i18n.language}
                            />
                        </div>
                    );
                }
                return '';
            },
            ...createDropdownFilter('categoryFilters', props.t, onNewFilterChange, dropdownFilters, (item: any) => ({
                key: item.categoryCode || item.key,
                value: item.categoryCode || item.value,
                text: item.categoryText || item.text,
            })),
        },
        {
            field: 'optionCode',
            key: 'optionCode',
            headerName: props.t("COLUMN_FACTORY:OPTIONCODE"),
            width: getWidth('optionCode', PSL_COL_RESIZE),
            sortable: false,
            editable: false,
            cellClass: 'ag-grid-row-style',
            hide: !visibleColumnKeys.includes('optionCode'), // Set hide based on visibleColumns
            cellRenderer: function (params: any) {
                if (params.data) {
                    return (
                        <div style={{display: "flex", alignItems: "center"}}>
                            {
                                !params.data.pccdFlag &&
                                <div>
                                    <PCCDTextComparePopup
                                        item={params.data}
                                        t={props.t}
                                    />
                                </div>
                            }
                            {/* {
                                buildTextExportStatusComponent((params.data[EOBColumnKey.ExportStatus]) as ITextExportStatus)
                            } */}
                            <div style={{width: '100%'}}>
                                <TextInput
                                    value={params.data.optionBasicView}
                                    onChange={() => {
                                        // No inline editing
                                    }}
                                    readOnly={true}
                                    uglyMode={false}
                                    rowIndex={0}
                                    t={props.t}
                                    language={props.i18n.language}
                                />
                            </div>
                        </div>
                    );
                }
                return '';
            },
            ...createTextFieldFilter('optionCode', props.t, onNewFilterChange),
        },
        {
            field: 'intSequence',
            key: 'intSequence',
            headerName: props.t("COLUMN_FACTORY:INTSEQUENCE"),
            width: getWidth('intSequence', PSL_COL_RESIZE),
            sortable: false,
            editable: false,
            cellClass: 'ag-grid-row-style',
            hide: !visibleColumnKeys.includes('intSequence'), // Set hide based on visibleColumns
            cellRenderer: function (params: any) {
                if (params.data) {
                    return (
                        <div style={{width: '100%'}}>
                            <TextInput
                                value={params.data.intSequence}
                                onChange={() => {
                                    // No inline editing
                                }}
                                readOnly={true}
                                uglyMode={false}
                                rowIndex={0}
                                t={props.t}
                                language={props.i18n.language}
                            />
                        </div>
                    );
                }
                return '';
            },
            ...createTextFieldFilter('intSequence', props.t, onNewFilterChange),
        },
        {
            field: 'pdiptext',
            key: 'pdiptext',
            headerName: props.t("COLUMN_FACTORY:PDIPTEXT"),
            width: getWidth('pdiptext', PSL_COL_RESIZE),
            sortable: false,
            editable: false,
            cellClass: 'ag-grid-row-style',
            hide: !visibleColumnKeys.includes('pdiptext'), // Set hide based on visibleColumns
            cellRenderer: function (params: any) {
                if (params.data) {
                    return (
                        <div style={{width: '100%'}}>
                            <TextInput
                                value={params.data.pdiptext}
                                onChange={() => {
                                    // No inline editing
                                }}
                                readOnly={true}
                                uglyMode={false}
                                rowIndex={0}
                                t={props.t}
                                language={props.i18n.language}
                            />
                        </div>
                    );
                }
                return '';
            },
            ...createTextFieldFilter('pdiptext', props.t, onNewFilterChange),
        },
        {
            field: 'shorttext',
            key: 'shorttext',
            headerName: props.t("COLUMN_FACTORY:SHORTTEXT"),
            width: getWidth('shorttext', PSL_COL_RESIZE),
            cellClass: 'ag-grid-row-style',
            sortable: false,
            editable: false,
            hide: !visibleColumnKeys.includes('shorttext'), // Set hide based on visibleColumns
            cellRenderer: function (params: any) {
                if (params.data) {
                    const { aiGeneratedShorttext, aiGeneratedLongtext, aiGeneratedFooter, aiGeneratedUsp } = getDeAiFlags(params.data);
                    return (
                        <div style={{display: "flex", alignItems: "center"}}>
                            <div style={{width: '100%'}}>
                                <TwoLanguageCompareInput
                                    t={props.t}
                                    isEditor={false}
                                    inEditMode={false}
                                    language={props.i18n.language}
                                    uglyMode={false}
                                    value={params.data.shorttext}
                                    onChange={(value: string) => {
                                        params.data.shorttext = value;
                                        onDataChanged('shorttext', params.rowIndex, value);
                                    }}
                                    readOnly={(isCellReadOnly ? isCellReadOnly('shorttext', params.data, props.readOnly) : props.readOnly)}
                                    rowIndex={0}
                                    globalSearch={globalSearch}
                                    isNewGrid={true}
                                    client={props.client}
                                    postOptions={props.postOptions}
                                    apiMethod={(client, postOptionss) => client.POST_optionAWSTranslate(postOptionss)}
                                    grammarApiMethod={(client, unusedPostOptions) =>
                                        props.baseLinks
                                            ? client.POST_awsBedrockGrammar(props.baseLinks)
                                            : { execute: () => Promise.reject("Links not loaded") }
                                    }
                                    pslAiGeneratedShorttext={aiGeneratedShorttext}
                                    pslAiGeneratedLongtext={aiGeneratedLongtext}
                                    aiGeneratedFooter={aiGeneratedFooter}
                                    aiGeneratedUsp={aiGeneratedUsp}
                                    editDialogHeader={{
                                        ...editDialogLabels,
                                        optionCode: params.data.optionBasicView ? params.data.optionBasicView as string : "",
                                        editingColumn: params.colDef.headerName || "",
                                        optionType: params.data.optionTypeView ? params.data.optionTypeView as string : "",
                                        pdipText: params.data.pdiptext as string
                                    }}

                                />
                            </div>
                        </div>
                    );
                }
                return '';
            },
            ...createTextFieldFilter('shorttext', props.t, onNewFilterChange),
        },
        {
            field: 'longtext',
            key: 'longtext', // Added key
            headerName: props.t("COLUMN_FACTORY:LONGTEXT"),
            width: getWidth('longtext', PSL_COL_RESIZE),
            cellClass: 'ag-grid-row-style',
            sortable: false,
            editable: false,
            hide: !visibleColumnKeys.includes('longtext'), // Set hide based on visibleColumns
            cellRenderer: function (params: any) {
                if (params.data) {
                    const hasShortTextDe = params.data.shorttext && params.data.shorttext.some((s: any) => s.flag === 'de' && s.value && s.value.trim());
                    const hasUspDe = params.data.usp && params.data.usp.some((u: any) => u.flag === 'de' && u.value && u.value.trim());
                    const longTextSuggestion = !!(hasShortTextDe && hasUspDe);
                    const { aiGeneratedShorttext, aiGeneratedLongtext, aiGeneratedFooter, aiGeneratedUsp } = getDeAiFlags(params.data);
                    return (
                        <div style={{display: "flex", alignItems: "center"}}>
                            <div style={{width: '100%'}}>
                                <TwoLanguageCompareTextArea
                                    t={props.t}
                                    inEditMode={false}
                                    language={props.i18n.language}
                                    uglyMode={false}
                                    value={params.data.longtext}
                                    onChange={(value: string) => {
                                        params.data.longtext = value;
                                        onDataChanged('longtext', params.rowIndex, value);
                                    }}
                                    readOnly={(isCellReadOnly ? isCellReadOnly('longtext', params.data, props.readOnly) : props.readOnly)}
                                    rowIndex={0}
                                    globalSearch={globalSearch}
                                    isNewGrid={true}
                                    client={props.client}
                                    postOptions={props.postOptions}
                                    apiMethod={(client, postOptionss) => client.POST_optionAWSTranslate(postOptionss)}
                                    grammarApiMethod={(client, unusedPostOptions) =>
                                        props.baseLinks
                                            ? client.POST_awsBedrockGrammar(props.baseLinks)
                                            : { execute: () => Promise.reject("Links not loaded") }
                                    }
                                    suggestionApiMethod={(client, unusedPostOptions) => {
                                        return client.GET_awsBedrockGenerateText(params.data.objectId);
                                    }}
                                    objectId={params.data.objectId}
                                    pslAiGeneratedShorttext={aiGeneratedShorttext}
                                    pslAiGeneratedLongtext={aiGeneratedLongtext}
                                    aiGeneratedFooter={aiGeneratedFooter}
                                    aiGeneratedUsp={aiGeneratedUsp}
                                    longTextSuggestion={longTextSuggestion}
                                    editDialogHeader={{
                                        ...editDialogLabels,
                                        ...editDialogGenerateLabels,
                                        optionCode: params.data.optionBasicView ? params.data.optionBasicView as string : "",
                                        editingColumn: params.colDef.headerName || "",
                                        optionType: params.data.optionTypeView ? params.data.optionTypeView as string : "",
                                        pdipText: params.data.pdiptext as string
                                    }}
                                />
                            </div>
                        </div>
                    );
                }
                return '';
            },
            ...createTextFieldFilter('longtext', props.t, onNewFilterChange),
        },
        {
            field: 'footer',
            key: 'footer', // Added key
            headerName: props.t("COLUMN_FACTORY:FOOTER"),
            width: getWidth('footer', PSL_COL_RESIZE),
            cellClass: 'ag-grid-row-style',
            sortable: false,
            editable: false,
            hide: !visibleColumnKeys.includes('footer'), // Set hide based on visibleColumns
            cellRenderer: function (params: any) {
                if (params.data) {
                    const { aiGeneratedShorttext, aiGeneratedLongtext, aiGeneratedFooter, aiGeneratedUsp } = getDeAiFlags(params.data);
                    return (
                        <div style={{display: "flex", alignItems: "center"}}>
                            <div style={{width: '100%'}}>
                                <TwoLanguageTextArea
                                    t={props.t}
                                    language={props.i18n.language}
                                    uglyMode={false}
                                    value={params.data.footer}
                                    onChange={(value: string) => {
                                        params.data.footer = value;
                                        onDataChanged('footer', params.rowIndex, value);
                                    }}
                                    readOnly={isCellReadOnly ? isCellReadOnly('footer', params.data, props.readOnly) : props.readOnly}
                                    rowIndex={0}
                                    globalSearch={globalSearch}
                                    isNewGrid={true}
                                    client={props.client}
                                    postOptions={props.postOptions}
                                    apiMethod={(client, postOptionss) => client.POST_optionAWSTranslate(postOptionss)}
                                    grammarApiMethod={(client, unusedPostOptions) =>
                                        props.baseLinks
                                            ? client.POST_awsBedrockGrammar(props.baseLinks)
                                            : { execute: () => Promise.reject("Links not loaded") }
                                    }
                                    pslAiGeneratedShorttext={aiGeneratedShorttext}
                                    pslAiGeneratedLongtext={aiGeneratedLongtext}
                                    aiGeneratedFooter={aiGeneratedFooter}
                                    aiGeneratedUsp={aiGeneratedUsp}
                                    editDialogHeader={{
                                        ...editDialogLabels,
                                        optionCode: params.data.optionBasicView ? params.data.optionBasicView as string : "",
                                        editingColumn: params.colDef.headerName || "",
                                        optionType: params.data.optionTypeView ? params.data.optionTypeView as string : "",
                                        pdipText: params.data.pdiptext as string
                                    }}
                                />
                            </div>
                        </div>
                    );
                }
                return '';
            },
            ...createTextFieldFilter('footer', props.t, onNewFilterChange),
        },
        {
            field: 'usp',
            key: 'usp', // Added key
            headerName: props.t("COLUMN_FACTORY:USP"),
            width: getWidth('usp', PSL_COL_RESIZE),
            cellClass: 'ag-grid-row-style',
            sortable: false,
            editable: false,
            hide: !visibleColumnKeys.includes('usp'), // Set hide based on visibleColumns
            cellRenderer: function (params: any) {
                if (params.data) {
                    const { aiGeneratedShorttext, aiGeneratedLongtext, aiGeneratedFooter, aiGeneratedUsp } = getDeAiFlags(params.data);
                    return (
                        <div style={{display: "flex", alignItems: "center"}}>
                            <div style={{width: '100%'}}>
                                <TwoLanguageTextArea
                                    t={props.t}
                                    language={props.i18n.language}
                                    uglyMode={false}
                                    value={params.data.usp}
                                    onChange={(value: string) => {
                                        params.data.usp = value;
                                        onDataChanged('usp', params.rowIndex, value);
                                    }}
                                    readOnly={isCellReadOnly ? isCellReadOnly('usp', params.data, props.readOnly) : props.readOnly}
                                    rowIndex={0}
                                    globalSearch={globalSearch}
                                    isNewGrid={true}
                                    client={props.client}
                                    postOptions={props.postOptions}
                                    apiMethod={(client, postOptionss) => client.POST_optionAWSTranslate(postOptionss)}
                                    grammarApiMethod={(client, unusedPostOptions) =>
                                        props.baseLinks
                                            ? client.POST_awsBedrockGrammar(props.baseLinks)
                                            : { execute: () => Promise.reject("Links not loaded") }
                                    }
                                    pslAiGeneratedShorttext={aiGeneratedShorttext}
                                    pslAiGeneratedLongtext={aiGeneratedLongtext}
                                    aiGeneratedFooter={aiGeneratedFooter}
                                    aiGeneratedUsp={aiGeneratedUsp}
                                    editDialogHeader={{
                                        ...editDialogLabels,
                                        optionCode: params.data.optionBasicView ? params.data.optionBasicView as string : "",
                                        editingColumn: params.colDef.headerName || "",
                                        optionType: params.data.optionTypeView ? params.data.optionTypeView as string : "",
                                        pdipText: params.data.pdiptext as string
                                    }}
                                />
                            </div>
                        </div>
                    );
                }
                return '';
            },
            ...createTextFieldFilter('usp', props.t, onNewFilterChange),
        },
        {

            field: 'optionIndicators',
            key: 'optionIndicators', // Added key
            headerName: props.t("GLOBAL:OPTIONINDICATORS"),
            width: getWidth('optionIndicators', PSL_COL_RESIZE),
            cellClass: 'ag-grid-row-style',
            sortable: false,
            editable: false,
            hide: !visibleColumnKeys.includes('optionIndicators'), // Set hide based on visibleColumns
            cellRenderer: function (params: any) {
                if (params.data) {
                    return (
                        <div>
                            <OptionIndicatorCell
                                t={props.t}
                                language={props.i18n.language}
                                uglyMode={false}
                                value={params.data.optionIndicators}
                                onChange={(value: string) => {
                                    params.data.optionIndicators = value;
                                    onDataChanged('optionIndicators', params.rowIndex, value);
                                }}
                                inEditMode={true}
                                readOnly={isCellReadOnly ? isCellReadOnly('optionIndicators', params.data, props.readOnly) : props.readOnly}
                                rowIndex={0}
                            />
                        </div>
                    );

                }
                return '';
            },
            ...createTextFieldFilter('optionIndicators', props.t, onNewFilterChange),
        },
        {
            field: 'derivateStates',
            key: 'derivateStates', // Added key
            editable: false,
            resizable: true,
            suppressAutoSize: true,
            filter: false,
            headerComponentFramework: () => (
                <div className="grid-header-derivate-cell grid-border-top grid-border-bottom" style={{width: '100%'}}>
                    <DerivateHeader
                        derivates={visibleDerivates}
                        derivateToColorMap={props.derivateToColorMap}
                        isBasicMarketLoaded={
                            props.loadedPSL !== undefined &&
                            props.loadedPSL.cnr.cnr === PropertiesKey.BasicMarketCnrKey
                        }
                    />
                </div>
            ),
            cellClass: 'ag-grid-row-style custom-row-no-padding',
            headerClass: "custom-header-no-padding", // Apply custom header class
            sortable: false,
            cellRenderer: function (params: any) {
                if (params.data) {
                    const derivateReadOnly = isCellReadOnly ? isCellReadOnly('derivateStates', params.data, props.readOnly) : props.readOnly;
                    return (
                        <div
                            className={'grid-border-bottom  grid-white-row '}
                            style={{width: '100%', display: 'flex', flexGrow: 1, flexBasis: '50px', }}
                            onClick={() => !derivateReadOnly
                                && handleOnCellClick('derivateStates', params.rowIndex, params.data.derivateStates, Array.from(visibleDerivates.values()))}
                        >
                            <Derivate
                                value={params.data.derivateStates}
                                derivates={visibleDerivates}
                                onChange={() => {
                                    // read-only derivate cell; no inline editing
                                }}
                                readOnly={true}
                                uglyMode={false}
                                rowIndex={0}
                                t={props.t}
                                language={props.i18n.language}
                                derivateToColorMap={props.derivateToColorMap}
                                isNewGridTable={true}
                                missingPDiPAMTBs={isSpecificPSL ? params.data[MissingPDiPAMTBsKey] as Set<string> : undefined}
                            />
                        </div>
                    );

                }
                return '';
            },
        },
        {
            field: 'workflowFilters',
            key: 'statusView',
            headerName: props.t("GLOBAL:WORKFLOW"),
            width: getWidth('workflowFilters', PSL_COL_RESIZE),
            cellClass: 'ag-grid-row-style',
            sortable: false,
            editable: false,
            hide: !visibleColumnKeys.includes('statusView'), // Set hide based on visibleColumns
            cellRenderer: (params: any): JSX.Element | string => {
                if (params.data) {
                    const itemAny = params.data as any;
                    let extractedOptionBasic = undefined;
                    if (itemAny.optionIndicators && itemAny.optionIndicators.dto) {
                        extractedOptionBasic = itemAny.optionIndicators.dto.optionBasic;
                    }
                    const currentStatusView = params.data.statusView;
                    return (
                        <NewWorkFlowState
                            t={props.t}
                            rowIndex={params.rowIndex}
                            derivates={new Map()}
                            language={props.i18n.language}
                            uglyMode={false}
                            workflowReadOnly={workFlowResetStateDisabled && workFlowResetStateDisabled('statusView', params.data, props.readOnly)}
                            value={{
                                ...currentStatusView,
                                optionBasic: extractedOptionBasic
                            }}
                            allWorkflows={props.allWorkflows}
                            onChange={(newValue: any) => {
                                // Needs to be implemented
                                params.data.statusView = newValue;
                                onDataChanged('statusView', params.rowIndex, newValue);
                            }}
                            onApprove={(newValue: IPSLApproval) => onApprove && onApprove(newValue, params.rowIndex)}
                            readOnly={isCellReadOnly ? isCellReadOnly('statusView', params.data, props.readOnly) : props.readOnly}
                            inEditMode={true}
                            onChangeWorkflow={() => onChangeWorkflow && onChangeWorkflow('statusView', params.data.objectId, params.rowIndex)}
                            onOptionWFReset={() => onOptionWFReset && onOptionWFReset(params.rowIndex)}
                            roles={props.roles}
                            i18n={i18next}
                        />

                    );
                }
                return '';
            },
            ...createDropdownFilter(
                'uniqueWorkflowFilters',
                props.t,
                onNewFilterChange,
                dropdownFilters,
                (item: any) => ({
                    key: `${item.wfId}${item.wfStatusId}` || item.key,
                    value: `${item.wfStatusId}` || item.value,
                    text: item.wfStatusName || item.text,
                })), // Use createDropdownFilter
        },
        {
            field: 'comment',
            key: 'comment', // Added key
            headerName: props.t("COLUMN_FACTORY:COMMENT"),
            width: getWidth('comment', PSL_COL_RESIZE),
            cellClass: 'ag-grid-row-style',
            sortable: false,
            editable: false,
            hide: !visibleColumnKeys.includes('comment'), // Set hide based on visibleColumns
            cellRenderer: function (params: any) {
                if (params.data) {
                    return (
                        <div style={{display: "flex", alignItems: "center"}}>
                            <div style={{width: '100%'}}>
                                <CommentSuggestionsTextArea
                                    value={params.data.comment}
                                    onChange={(value: string, aiGenerated?: boolean) => {
                                        params.data.comment = value;
                                        if (aiGenerated !== undefined) {
                                            params.data.aiGeneratedComment = aiGenerated;
                                        }
                                        onDataChanged('comment', params.rowIndex, value);
                                    }}
                                    readOnly={isCellReadOnly ? isCellReadOnly('comment', params.data, props.readOnly) : props.readOnly}
                                    client={props.client}
                                    postOptions={props.postOptions}
                                    aiGeneratedComment={params.data.aiGeneratedComment || false}
                                    labelSuggestions={props.t('OPTIONS_VIEW:COMMENTS_SUGGESTION_LABEL')}
                                    labelLoading={props.t('OPTIONS_VIEW:COMMENTS_SUGGESTION_LOADING')}
                                    labelNa={props.t('OPTIONS_VIEW:COMMENTS_SUGGESTION_NA')}
                                    labelNone={props.t('OPTIONS_VIEW:COMMENTS_SUGGESTION_NONE')}
                                    labelPlaceholder={props.t('OPTIONS_VIEW:COMMENTS_SUGGESTION_PLACEHOLDER')}
                                    labelFailed={props.t('OPTIONS_VIEW:COMMENTS_SUGGESTION_FAILED')}
                                />
                            </div>
                        </div>
                    );

                }
                return '';
            },
            ...createTextFieldFilter('comment', props.t, onNewFilterChange),
        },
        {
            field: 'reference',
            key: 'reference', // Added key
            headerName: props.t("COLUMN_FACTORY:REFERENCE"),
            width: getWidth('reference', PSL_COL_RESIZE),
            cellClass: 'ag-grid-row-style',
            sortable: false,
            editable: false,
            hide: !visibleColumnKeys.includes('reference'), // Set hide based on visibleColumns
            cellRenderer: function (params: any) {
                if (params.data) {
                    return (
                        <div style={{display: "flex", alignItems: "center"}}>
                            <div style={{width: '100%'}}>
                                <TextArea
                                    t={props.t}
                                    language={props.i18n.language}
                                    uglyMode={false}
                                    value={params.data.reference}
                                    onChange={(value: string) => {
                                        params.data.reference = value;
                                        onDataChanged('reference', params.rowIndex, value);
                                    }}
                                    readOnly={isCellReadOnly ? isCellReadOnly('reference', params.data, props.readOnly) : props.readOnly}
                                    rowIndex={0}

                                />
                            </div>
                        </div>
                    );

                }
                return '';
            },
            ...createTextFieldFilter('reference', props.t, onNewFilterChange),
        },
        {
            field: 'referenceExc',
            key: 'referenceExc', // Added key
            headerName: props.t("COLUMN_FACTORY:REFERENCEEXC"),
            width: getWidth('referenceExc', PSL_COL_RESIZE),
            cellClass: 'ag-grid-row-style',
            sortable: false,
            editable: false,
            hide: !visibleColumnKeys.includes('referenceExc'), // Set hide based on visibleColumns
            cellRenderer: function (params: any) {
                if (params.data) {
                    return (
                        <div style={{display: "flex", alignItems: "center"}}>
                            <div style={{width: '100%'}}>
                                <TextArea
                                    t={props.t}
                                    language={props.i18n.language}
                                    uglyMode={false}
                                    value={params.data.referenceExc}
                                    onChange={(value: string) => {
                                        params.data.referenceExc = value;
                                        onDataChanged('referenceExc', params.rowIndex, value);
                                    }}
                                    readOnly={isCellReadOnly ? isCellReadOnly('referenceExc', params.data, props.readOnly) : props.readOnly}
                                    rowIndex={0}

                                />
                            </div>
                        </div>
                    );

                }
                return '';
            },
            ...createTextFieldFilter('referenceExc', props.t, onNewFilterChange),
        },

        {
            field: 'availableStart',
            key: 'availableStart', // Added key
            headerName: props.t("COLUMN_FACTORY:AVAILABLESTART"),
            width: getWidth('availableStart', PSL_COL_RESIZE),
            cellClass: 'ag-grid-row-style',
            sortable: false,
            editable: false,
            hide: !visibleColumnKeys.includes('availableStart'), // Set hide based on visibleColumns
            cellRenderer: function (params: any) {
                if (params.data) {
                    return (
                        <div style={{display: "flex", alignItems: "center"}}>
                            <div style={{width: '100%'}}>
                                <DateInput
                                    t={props.t}
                                    language={props.i18n.language}
                                    uglyMode={false}
                                    value={params.data.availableStart || ''}
                                    onChange={(value: string) => {
                                        params.data.availableStart = value;
                                        onDataChanged('availableStart', params.rowIndex, value);
                                    }}
                                    readOnly={isCellReadOnly ? isCellReadOnly('availableStart', params.data, props.readOnly) : props.readOnly}
                                    rowIndex={0}

                                />
                            </div>
                        </div>
                    );

                }
                return '';
            },
            ...createDateFieldFilter('availableStart', props.t, onNewFilterChange),
        },
        {
            field: 'availableEnd',
            key: 'availableEnd', // Added key
            headerName: props.t("COLUMN_FACTORY:AVAILABLEEND"),
            width: getWidth('availableEnd', PSL_COL_RESIZE),
            cellClass: 'ag-grid-row-style',
            sortable: false,
            editable: false,
            hide: !visibleColumnKeys.includes('availableEnd'), // Set hide based on visibleColumns
            cellRenderer: function (params: any) {
                if (params.data) {
                    return (
                        <div style={{display: "flex", alignItems: "center"}}>
                            <div style={{width: '100%'}}>
                                <DateInput
                                    t={props.t}
                                    language={props.i18n.language}
                                    uglyMode={false}
                                    value={params.data.availableEnd || ''}
                                    onChange={(value: string) => {
                                        params.data.availableEnd = value;
                                        onDataChanged('availableEnd', params.rowIndex, value);
                                    }}
                                    readOnly={isCellReadOnly ? isCellReadOnly('availableEnd', params.data, props.readOnly) : props.readOnly}
                                    rowIndex={0}

                                />
                            </div>
                        </div>
                    );

                }
                return '';
            },
            ...createDateFieldFilter('availableEnd', props.t, onNewFilterChange)
        },
        {
            field: 'constraint',
            key: 'constraint', // Added key
            headerName: props.t("COLUMN_FACTORY:CONSTRAINT"),
            width: getWidth('constraint', PSL_COL_RESIZE),
            cellClass: 'ag-grid-row-style',
            sortable: false,
            editable: false,
            hide: !visibleColumnKeys.includes('constraint'), // Set hide based on visibleColumns
            cellRenderer: function (params: any) {
                if (params.data) {
                    return (
                        <div style={{display: "flex", alignItems: "center"}}>
                            <div style={{width: '100%'}}>
                                <OnClickInput
                                    t={props.t}
                                    language={props.i18n.language}
                                    uglyMode={false}
                                    value={params.data.constraint}
                                    onChange={(value: string) => {
                                        params.data.constraint = value;
                                    }}
                                    readOnly={true}
                                    rowIndex={0}

                                />
                            </div>
                        </div>
                    );

                }
                return '';
            },
            ...createTextFieldFilter('constraint', props.t, onNewFilterChange),
        },
        {
            field: 'exclusion',
            key: 'exclusion', // Added key
            headerName: props.t("COLUMN_FACTORY:EXCLUSION"),
            width: getWidth('exclusion', PSL_COL_RESIZE),
            cellClass: 'ag-grid-row-style',
            sortable: false,
            editable: false,
            hide: !visibleColumnKeys.includes('exclusion'), // Set hide based on visibleColumns
            cellRenderer: function (params: any) {
                if (params.data) {
                    return (
                        <div style={{display: "flex", alignItems: "center"}}>
                            <div style={{width: '100%'}}>
                                <OnClickInput
                                    t={props.t}
                                    language={props.i18n.language}
                                    uglyMode={false}
                                    value={params.data.exclusion}
                                    onChange={(value: string) => {
                                        params.data.exclusion = value;
                                    }}
                                    readOnly={true}
                                    rowIndex={0}

                                />
                            </div>
                        </div>
                    );

                }
                return '';
            },
            ...createTextFieldFilter('exclusion', props.t, onNewFilterChange),
        },
        {
            field: 'packageContent',
            key: 'packageContent', // Added key
            headerName: props.t("COLUMN_FACTORY:PACKAGECONTENT"),
            width: getWidth('packageContent', PSL_COL_RESIZE),
            cellClass: 'ag-grid-row-style',
            sortable: false,
            editable: false,
            hide: !visibleColumnKeys.includes('packageContent'), // Set hide based on visibleColumns
            cellRenderer: function (params: any) {
                if (params.data) {
                    const columnIndex = visibleColumns.findIndex((col: any) => col.key === 'packageContent');
                    return (
                        <div style={{display: "flex", alignItems: "center"}}>
                            <div style={{width: '100%'}}>
                                <OnClickInput
                                    t={props.t}
                                    language={props.i18n.language}
                                    uglyMode={false}
                                    value={params.data.packageContent}
                                    onCellClick={() => handleOnCellClick('packageContent', params.rowIndex, params.data.packageContent, [])}
                                    onChange={(value: string) => {
                                        params.data.packageContent = value;
                                    }}
                                    editDialogHeader={{
                                        ...editDialogLabels,
                                        optionCode: params.data.optionBasicView ? params.data.optionBasicView as string : "",
                                        editingColumn: visibleColumns[columnIndex - 1].label,
                                        optionType: params.data.optionTypeView ? params.data.optionTypeView as string : "",
                                        pdipText: params.data.pdiptext as string
                                    }}
                                    readOnly={true}
                                    rowIndex={0}

                                />
                            </div>
                        </div>
                    );

                }
                return '';
            },
            ...createTextFieldFilter('packageContent', props.t, onNewFilterChange),
        },
        {
            field: 'price',
            key: 'price', // Added key
            headerName: props.t("COLUMN_FACTORY:PRICE"),
            width: getWidth('price', PSL_COL_RESIZE),
            cellClass: 'ag-grid-row-style',
            sortable: false,
            editable: false,
            hide: !visibleColumnKeys.includes('price'), // Set hide based on visibleColumns
            cellRenderer: function (params: any) {
                if (params.data) {
                    return (
                        <div style={{display: "flex", alignItems: "center"}}>
                            <div style={{width: '100%'}}>
                                <PriceInput
                                    t={props.t}
                                    language={props.i18n.language}
                                    uglyMode={false}
                                    value={params.data.price}
                                    onChange={(value: string) => {
                                        params.data.price = value;
                                        onDataChanged('price', params.rowIndex, value);
                                    }}
                                    readOnly={isCellReadOnly ? isCellReadOnly('price', params.data, props.readOnly) : props.readOnly}
                                    rowIndex={0}

                                />
                            </div>
                        </div>
                    );

                }
                return '';
            },
            ...createTextFieldFilter('price', props.t, onNewFilterChange),
        },
        {
            field: 'optionTypeFilters',
            key: 'optionTypeView',
            headerName: props.t("COLUMN_FACTORY:OPTIONTYPEVIEW"),
            width: getWidth('optionTypeFilters', PSL_COL_RESIZE),
            cellClass: 'ag-grid-row-style',
            sortable: false,
            editable: false,
            hide: !visibleColumnKeys.includes('optionTypeView'), // Set hide based on visibleColumns
            cellRenderer: function (params: any) {
                if (params.data) {
                    return (
                        <div style={{display: "flex", alignItems: "center"}}>
                            <div style={{width: '100%'}}>
                                <TextInput
                                    t={props.t}
                                    language={props.i18n.language}
                                    uglyMode={false}
                                    value={params.data.optionTypeView}
                                    onChange={(value: string) => {
                                        params.data.optionTypeView = value;
                                    }}
                                    readOnly={true}
                                    rowIndex={0}

                                />
                            </div>
                        </div>
                    );

                }
                return '';
            },
            ...createDropdownFilter('optionTypeFilters', props.t, onNewFilterChange, dropdownFilters, (item: any) => ({
                key: `${item.value}-${item.name}` || item.key,
                value: item.name || item.value,
                text: item.name || item.text,
            })),
        },
        {
            field: 'flags',
            key: 'flags', // Added key
            headerName: props.t("COLUMN_FACTORY:FLAGS"),
            width: getWidth('flags', PSL_COL_RESIZE),
            cellClass: 'ag-grid-row-style',
            sortable: false,
            editable: false,
            hide: !visibleColumnKeys.includes('flags'), // Set hide based on visibleColumns
            cellRenderer: function (params: any) {
                if (params.data) {
                    return (
                        <div style={{display: "flex", alignItems: "center"}}>
                            <div style={{width: '100%'}}>
                                <TextFlagCell
                                    t={props.t}
                                    language={props.i18n.language}
                                    uglyMode={false}
                                    value={params.data.flags}
                                    onChange={(value: string) => {
                                        const selectedFlags = flagsOptions.filter((flag: any) => value.includes(flag.objectId));
                                        params.data.flags.value = selectedFlags;
                                        onDataChanged('flags', params.rowIndex, value);
                                    }}
                                    readOnly={isCellReadOnly ? isCellReadOnly('flags', params.data, props.readOnly) : props.readOnly}
                                    rowIndex={0}

                                />
                            </div>
                        </div>
                    );

                }
                return '';
            },
            ...createDropdownFilter(
                'flags',
                props.t,
                onNewFilterChange,
                flagsOptions,
                (item: any) => ({
                    key: item.objectId || item.key,
                    value: item.objectId || item.value,
                    text: item.textLabel || item.text,
                }),
                true // Properly aligned argument
            ),
        },
        {
            field: 'changedBy',
            key: 'changedBy', // Added key
            headerName: props.t("COLUMN_FACTORY:CHANGEDBY"),
            width: getWidth('changedBy', PSL_COL_RESIZE),
            cellClass: 'ag-grid-row-style',
            sortable: false,
            editable: false,
            hide: !visibleColumnKeys.includes('changedBy'), // Set hide based on visibleColumns
            cellRenderer: function (params: any) {
                if (params.data) {
                    return (
                        <div style={{display: "flex", alignItems: "center"}}>
                            <div style={{width: '100%'}}>
                                <TextInput
                                    t={props.t}
                                    language={props.i18n.language}
                                    uglyMode={false}
                                    value={params.data.changedBy}
                                    onChange={(value: string) => {
                                        params.data.usp = value;
                                    }}
                                    readOnly={true}
                                    rowIndex={0}

                                />
                            </div>
                        </div>
                    );

                }
                return '';
            },
            ...createTextFieldFilter('changedBy', props.t, onNewFilterChange),
        },
        {
            field: 'changedTimestamp',
            key: 'changedTimestamp', // Added key
            headerName: props.t("COLUMN_FACTORY:CHANGEDTIMESTAMP"),
            width: getWidth('changedTimestamp', PSL_COL_RESIZE),
            cellClass: 'ag-grid-row-style',
            sortable: false,
            editable: false,
            hide: !visibleColumnKeys.includes('changedTimestamp'), // Set hide based on visibleColumns
            cellRenderer: function (params: any) {
                if (params.data) {
                    return (
                        <div style={{display: "flex", alignItems: "center"}}>
                            <div style={{width: '100%'}}>
                                <TextInput
                                    t={props.t}
                                    language={props.i18n.language}
                                    uglyMode={false}
                                    value={params.data.changedTimestamp}
                                    onChange={(value: string) => {
                                        params.data.usp = value;
                                    }}
                                    readOnly={true}
                                    rowIndex={0}

                                />
                            </div>
                        </div>
                    );

                }
                return '';
            },
            ...createDateFieldFilter('changedTimestamp', props.t, onNewFilterChange)
        },
    ];
};

export const getDefaultColDef: any = {
    // F2: autoHeight removed on purpose – it forced ag-grid to render + measure every cell to
    // compute row height (heavy layout thrashing). Row height is fixed via `rowHeight={100}` on the grid.
    // F9: dropped enterprise-only flags (enableRowGroup/enablePivot/enableValue, ignored in community)
    // and set editable:false to match the read-only grid (every column already overrides to false).
    editable: false,
    resizable: true,
    floatingFilter: true,
    suppressMenu: true,
    minWidth: 100,
    autoHeight: true,
    sortable: false,
};



// WEBPACK FOOTER //
// ./src/views/OptionsView/AgGridOptionColumns.tsx